import React, { useEffect, useMemo, useState } from 'react';
import { Clock3, Loader2, MapPin, RefreshCw, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { getPosActiveTimeLogs } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

function formatCheckIn(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleTimeString('de-AT', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function formatMinutes(value) {
  const total = Math.max(0, Number(value || 0));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours <= 0) return `${minutes} Min`;
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
}

export default function PosTimeTracking() {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const load = async (showSpinner = false) => {
    if (showSpinner) setIsRefreshing(true);
    else setIsLoading(true);

    try {
      const data = await getPosActiveTimeLogs();
      setEntries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load active time logs', error);
      toast.error(error?.response?.data?.detail || 'Zeiterfassung konnte nicht geladen werden');
      setEntries([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
    const intervalId = window.setInterval(() => {
      load(true);
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, []);

  const stats = useMemo(() => {
    const activeCount = entries.length;
    const totalMinutes = entries.reduce((sum, entry) => sum + Number(entry.minutes_active || 0), 0);
    const longestMinutes = entries.reduce((max, entry) => Math.max(max, Number(entry.minutes_active || 0)), 0);
    return {
      activeCount,
      totalMinutes,
      longestMinutes,
      locationName: entries[0]?.location_name || '—',
    };
  }, [entries]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-muted">
        <Loader2 className="h-8 w-8 animate-spin text-[#18323b]" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-muted p-4 lg:p-6" data-testid="pos-time-tracking">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Workforce</p>
          <h1 className="mt-1 text-2xl font-black tracking-tight text-[#18323b] lg:text-4xl">Zeiterfassung</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Aktive Sessions aus dem zweiten Supabase-Projekt
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => load(true)}
          disabled={isRefreshing}
          className="border-border bg-card text-muted-foreground hover:bg-muted"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card className="border-border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground">Aktiv</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-black tracking-tight text-[#18323b]">{stats.activeCount}</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground">Gesamtzeit</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-black tracking-tight text-[#18323b]">{formatMinutes(stats.totalMinutes)}</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground">Laengste Session</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-black tracking-tight text-[#18323b]">{formatMinutes(stats.longestMinutes)}</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground">Standort</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-bold tracking-tight text-[#18323b]">{stats.locationName}</p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
        {entries.map((entry) => (
          <Card key={entry.id} className="border-border bg-card shadow-sm">
            <CardContent className="space-y-3 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-black tracking-tight text-[#18323b]">
                    {entry.employee_name || 'Unbekannt'}
                  </p>
                  <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" />
                    <span>{entry.location_name || 'Standort'}</span>
                  </div>
                </div>
                <span className="rounded-full bg-[#eef4e8] px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#5c8a2f]">
                  Aktiv
                </span>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-border bg-muted px-3 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Check-in</p>
                  <p className="mt-1 text-base font-bold text-[#18323b]">{formatCheckIn(entry.check_in)}</p>
                </div>
                <div className="rounded-xl border border-border bg-muted px-3 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Laufzeit</p>
                  <p className="mt-1 text-base font-bold text-[#18323b]">{formatMinutes(entry.minutes_active)}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <UserRound className="h-4 w-4" />
                <span>ID {entry.profile_id || '—'}</span>
              </div>

              {entry.note ? (
                <div className="rounded-xl border border-border bg-muted px-3 py-3 text-sm text-muted-foreground">
                  {entry.note}
                </div>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>

      {entries.length === 0 ? (
        <Card className="mt-6 border-border bg-card shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Clock3 className="mb-3 h-12 w-12 text-slate-200" />
            <p className="text-lg font-semibold text-[#18323b]">Keine aktiven Zeiterfassungen</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Fuer den konfigurierten Workforce-Standort sind aktuell keine offenen Sessions vorhanden.
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
